import { zodResolver } from '@hookform/resolvers/zod';
import { nonEmptyArray } from '@utils/zodHelpers';
import { useMemo, useState } from 'react';
import { useFieldArray, useForm } from 'react-hook-form';
import type { UserFilters, UserResponse } from 'stream-chat';
import { useChatContext } from 'stream-chat-react';
import z from 'zod';
import { ErrorMessage } from '~/components/form-fields/ErrorMessage';
import { RequestButton } from '~/components/utils/buttons';
import { useStreamChatContext } from '~/react_context/StreamChatContext';
import StreamChatUserSelectList from './StreamChatUserSelectList';

const formSchema = z.object({
	members: nonEmptyArray(
		z.object({
			id: z.string(),
			image: z.string().optional(),
			name: z.string().optional(),
		})
	),
});
type FormType = z.infer<typeof formSchema>;

export default function AddUsersToChannelForm() {
	const { channel, client } = useChatContext();
	const { closeChatOverlay } = useStreamChatContext();
	const [isAddingUsers, setIsAddingUsers] = useState(false);
	const {
		control,
		formState: { errors },
		handleSubmit,
	} = useForm<FormType>({
		resolver: zodResolver(formSchema),
	});
	const {
		append: addMember,
		fields: selectedMembers,
		remove: removeMember,
	} = useFieldArray({
		control,
		name: 'members',
	});

	const onSubmit = handleSubmit(async (data) => {
		if (!channel) return;

		setIsAddingUsers(true);

		await channel.addMembers(data.members.map((member) => member.id));

		setIsAddingUsers(false);
		closeChatOverlay();
	}, console.warn);

	// @ts-ignore $nin doesn't exist in the type definition but is supported
	const usersFilter = useMemo<UserFilters>(() => {
		if (!channel) return {};
		const existingChannelMemberUserIds = Object.keys(channel.state.members);
		return {
			id: { $nin: [client.userID!, ...existingChannelMemberUserIds] },
		};
	}, [channel, client]);

	if (!channel) return null;

	const onUserDeselected = (user: UserResponse) => removeMember(selectedMembers.findIndex((member) => member.id === user.id));
	const onUserSelected = (user: UserResponse) => addMember({ id: user.id, image: user.image, name: user.name });
	return (
		<form className="h-full" onSubmit={onSubmit}>
			<h1 className="text-lg mb-4">Add Users To Channel</h1>
			<div className="overflow-y-auto h-[calc(100%-100px)]">
				<div className="my-4">
					<StreamChatUserSelectList onUserDeselected={onUserDeselected} onUserSelected={onUserSelected} userFilter={usersFilter} />
				</div>
				<ErrorMessage errorMessage={errors?.members?.message} />
			</div>
			<div className="my-4 flex items-center justify-between">
				<button
					type="button"
					className="rounded-md border border-gray-300 bg-white px-4 py-2 text-base font-medium text-gray-700 shadow-xs hover:bg-gray-50 focus:outline-hidden focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 sm:col-start-1 sm:mt-0 sm:text-lg"
					onClick={closeChatOverlay}
				>
					Cancel
				</button>
				<RequestButton
					className="rounded-md border border-transparent bg-primary px-4 py-2 text-base font-medium text-white shadow-xs hover:bg-primary-dark focus:outline-hidden focus:ring-2 focus:bg-primary focus:ring-offset-2 sm:col-start-2 sm:text-lg disabled:cursor-not-allowed disabled:bg-gray-300 disabled:text-gray-600"
					isLoading={isAddingUsers}
				>
					Add Users
				</RequestButton>
			</div>
		</form>
	);
}
